/**
 * @file API client for the admin HI-alert review surface served by the
 *   authentication service (`/admin/hi-alerts`).
 * @module api/admin/hiAlertsApi
 *
 * B1.3H-3R-3 — consumes the existing authentication endpoints for the
 * same-patient `ihi-revalidation-review` type (UC.015 / HI_015_005818). All
 * calls target the authentication base URL with the standard bearer token.
 * B3.3 (CU-14ygtkj8z2x) — list/detail/resolve are typed on the shared
 * three-type review union; endpoint paths and request semantics are unchanged. The
 * amended-search body is built explicitly from the five backend-allowed keys so
 * no extra operator-entered field can leak into the request, and the criteria
 * stay transient (never cached, persisted or logged here).
 */

import { ENV } from "../../config/runtimeEnv";

import {
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_3,
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_4,
	AMEND_VARIANT_UC010_REGISTRATION_TYPE_5,
	AMEND_VARIANT_UC015_ADDRESS_TYPE_6,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_1,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_2,
	AMEND_VARIANT_UC015_MEDICARE_TYPE_3,
	AmendedSearchCriteria,
	AmendedSearchResult,
	HiAlertListResponse,
	HiAlertReview,
	RegistrationAmendedSearchRequest,
	RegistrationAmendedSearchResult,
	RevalidationAmendedSearchRequest,
	RevalidationAmendedSearchResult,
} from "../../types/admin/hiAlerts";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface ListHiAlertsParams {
	type?: string;
	status?: string;
	page?: number;
	perPage?: number;
}

/** List company-scoped HI alerts/reviews (defaults to open on the backend). */
export const listHiAlerts = async (params: ListHiAlertsParams = {}): Promise<HiAlertListResponse> => {
	const response = await http.get(`${API_BASE_URL}/admin/hi-alerts`, {
		params: {
			...(params.type ? { type: params.type } : {}),
			...(params.status ? { status: params.status } : {}),
			...(params.page ? { page: params.page } : {}),
			...(params.perPage ? { per_page: params.perPage } : {}),
		},
	});
	return response.data;
};

/** Privacy-safe detail for a single company-scoped review. */
export const getHiAlert = async (type: string, id: number): Promise<HiAlertReview> => {
	const response = await http.get(`${API_BASE_URL}/admin/hi-alerts/${type}/${id}`);
	return response.data.data;
};

/**
 * Submit an operator-initiated amended DVA IHI revalidation search for an open
 * no-match review. Sends ONLY the five backend-allowed keys — never spread the
 * form object — so the request body cannot carry any other field.
 */
export const submitAmendedSearch = async (
	type: string,
	id: number,
	criteria: AmendedSearchCriteria
): Promise<AmendedSearchResult> => {
	const body = {
		family_name: criteria.family_name,
		given_name: criteria.given_name,
		date_of_birth: criteria.date_of_birth,
		sex: criteria.sex,
		dva_file_number: criteria.dva_file_number,
	};

	const response = await http.post(
		`${API_BASE_URL}/admin/hi-alerts/${type}/${id}/amended-search`,
		body
	);
	return response.data.data;
};

/**
 * CU-14ygtkj8z3m (HI_010_005818): the exact outbound body for each registration
 * variant. Every key is written out explicitly and the form object is never
 * spread, so the pinned Search Type's allow-list is the only thing that can leave
 * the client — no selector, IRN, IHI, other-technique identifier or extra field.
 */
const registrationAmendedSearchBody = (request: RegistrationAmendedSearchRequest): Record<string, string> => {
	switch (request.variant) {
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_3:
			return {
				medicare_card_number: request.criteria.medicare_card_number,
				given_name: request.criteria.given_name,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_4:
			return {
				dva_file_number: request.criteria.dva_file_number,
				given_name: request.criteria.given_name,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
		case AMEND_VARIANT_UC010_REGISTRATION_TYPE_5:
			return {
				dva_file_number: request.criteria.dva_file_number,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
	}
};

/**
 * Submit an operator-initiated UC.010 registration IHI search resubmission for an
 * open registration no-match review. Same endpoint as the UC.015 action; the
 * backend dispatches on the stored review reason, and this client sends only the
 * variant's exact criteria.
 */
export const submitRegistrationAmendedSearch = async (
	type: string,
	id: number,
	request: RegistrationAmendedSearchRequest
): Promise<RegistrationAmendedSearchResult> => {
	const response = await http.post(
		`${API_BASE_URL}/admin/hi-alerts/${type}/${id}/amended-search`,
		registrationAmendedSearchBody(request)
	);
	return response.data.data;
};

/** CU-14ygtkj98pb (HI_015_005818): every key is written out per variant and the form object is never spread. */
const revalidationAmendedSearchBody = (request: RevalidationAmendedSearchRequest): Record<string, string> => {
	switch (request.variant) {
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_1:
			return {
				medicare_card_number: request.criteria.medicare_card_number,
				individual_reference_number: request.criteria.individual_reference_number,
				given_name: request.criteria.given_name,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_2:
			return {
				medicare_card_number: request.criteria.medicare_card_number,
				individual_reference_number: request.criteria.individual_reference_number,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
		case AMEND_VARIANT_UC015_MEDICARE_TYPE_3:
			return {
				medicare_card_number: request.criteria.medicare_card_number,
				given_name: request.criteria.given_name,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
			};
		case AMEND_VARIANT_UC015_ADDRESS_TYPE_6:
			return {
				given_name: request.criteria.given_name,
				family_name: request.criteria.family_name,
				date_of_birth: request.criteria.date_of_birth,
				sex: request.criteria.sex,
				address: request.criteria.address,
			};
	}
};

/** CU-14ygtkj98pb: same endpoint; the backend dispatches on the stored review contract, never on a client-sent type. */
export const submitRevalidationAmendedSearch = async (
	type: string,
	id: number,
	request: RevalidationAmendedSearchRequest
): Promise<RevalidationAmendedSearchResult> => {
	const response = await http.post(
		`${API_BASE_URL}/admin/hi-alerts/${type}/${id}/amended-search`,
		revalidationAmendedSearchBody(request)
	);
	return response.data.data;
};

/**
 * Resolve an open, company-scoped review through the existing authentication
 * resolve endpoint. The body is intentionally empty: this slice does not capture
 * operator resolution notes, so no free-text or identifier can ride along. Returns
 * the backend's category-only updated review projection. The backend stays
 * authoritative — it answers 409 for a non-open review and 404 for an
 * unknown/cross-company one — so this client adds no lifecycle logic of its own.
 */
export const resolveHiAlert = async (type: string, id: number): Promise<HiAlertReview> => {
	const response = await http.post(
		`${API_BASE_URL}/admin/hi-alerts/${type}/${id}/resolve`,
		{}
	);
	return response.data.data;
};
